import React from 'react';
import type { ImageProps } from '@archipro-design/aria';
import { Button, pxToRem, useStyles, VideoPlayer } from '@archipro-design/aria';
import * as S from './VideoBannerWithOptimistic.style';
import { Trash } from '@archipro-design/icons';
import { HoverActionArticle } from '@modules/page-builder/component/common/hover-action/HoverActionArticle';
import type { ColumnDimensionType } from '@modules/page-builder/types';
import PreviewImage from '@modules/page-builder/component/edit-article/page-builder-image/PreviewImage';
import { ClientOnly } from 'remix-utils';
import { useTracker } from '@archipro-website/tracker';

interface VideoBannerWithOptimisticProps {
    dimension: ColumnDimensionType;
    videoBannerUrl: string;
    imageUrl?: string;
    imageOptimistic?: string;
    onDelete?: () => void;
    playsInline: boolean;
    autoplay?: boolean;
    isDeleting?: boolean;
}

const VideoBannerWithOptimistic = ({
    dimension,
    videoBannerUrl,
    imageUrl,
    imageOptimistic,
    onDelete,
    playsInline,
    autoplay,
    isDeleting,
}: VideoBannerWithOptimisticProps) => {
    const { css } = useStyles({ dimension });
    const [loaded, setLoaded] = React.useState(!!imageUrl);
    const [optimisticImage, setOptimisticImage] =
        React.useState(imageOptimistic);

    const tracker = useTracker();

    const imageProps: Omit<ImageProps, 'src'> = {
        layout: 'fill',
        width: dimension.width,
        height: dimension.height,
        objectFit: 'cover',
        fadeIn: false,
        alt: '',
    };

    const videoPlayer = (
        <VideoPlayer
            src={videoBannerUrl}
            coverImage={{
                src: imageUrl ?? optimisticImage ?? '',
                width: dimension.width,
                height: dimension.height,
                layout: 'intrinsic',
                objectFit: 'cover',
                objectPosition: 'center',
                alt: '',
            }}
            muted={true}
            controls={true}
            playsInline={playsInline}
            onCoverLoadComplete={() => {
                setLoaded(true);
                setOptimisticImage(undefined);
            }}
            variables={{
                playIconSizeDesktop: 130,
                playIconSizeMobile: 62,
                coverImagePosition: 'absolute',
            }}
            playing={!!autoplay}
            trackPlayEvent={() => {
                tracker.log('Video', {
                    url: new URL(window.location.href),
                });
            }}
        />
    );

    return (
        <div className={css(S.AspectRatioBox)}>
            <>
                {playsInline ? (
                    <ClientOnly fallback={<></>}>
                        {() => videoPlayer}
                    </ClientOnly>
                ) : (
                    videoPlayer
                )}
                <div
                    className={`${css(S.HoverActions)} hover-buttons-image`}
                    style={{
                        pointerEvents: isDeleting ? 'none' : 'auto',
                    }}
                >
                    <HoverActionArticle
                        label={isDeleting ? 'Removing...' : 'Remove'}
                        action={
                            <Button
                                onClick={isDeleting ? undefined : onDelete}
                                icon={<Trash />}
                                size={24}
                                color={'white'}
                                disabled={isDeleting}
                                variables={{
                                    buttonPadding: pxToRem(8),
                                }}
                            />
                        }
                    />
                </div>
                <PreviewImage
                    show={loaded}
                    imageUrl={optimisticImage}
                    imageProps={imageProps}
                />
            </>
        </div>
    );
};

export default VideoBannerWithOptimistic;
